import { IRangePlanningItemsClient } from './IRangePlanningItemsClient';
import {
  RangePlanningItem,
  RangePlanningItemListResponse,
  CreateRangePlanningItemRequest,
  ItemLifecycleStatus,
} from './rangePlanningItemsTypes';
import { rangePlanningItems } from '../../../../mocks/data/rangePlanningItemsData';
import { CarbonRating } from '../recipeV2/recipeV2Types';
import { emptyNutritionalInfo, NutritionSource } from '../../../../components/shared/nutrition/types';

/** In-memory mocked client for the Range Planning Items demo screen, following the same
 * pattern as RecipeV2ClientMocked - no MSW handlers, just an in-code store seeded from
 * src/mocks/data/rangePlanningItemsData.ts. */
export class RangePlanningItemsClientMocked implements IRangePlanningItemsClient {
  private items: RangePlanningItem[] = [...rangePlanningItems];
  private nextId = Math.max(...rangePlanningItems.map((i) => i.id)) + 1;

  // NOTE: methods are arrow-function class fields (not prototype methods) so `this` stays
  // bound to the instance even when a consuming hook destructures them off the client object
  // (e.g. `const { getItems } = useRangePlanningItemsApi();`) - see menusClientMocked.ts for
  // the full explanation of this gotcha, found via the same bug affecting the Menus screen.
  getItems = async (search: string, category: string, page: number, pageSize: number): Promise<RangePlanningItemListResponse> => {
    const term = search.trim().toLowerCase();
    const filtered = this.items.filter((item) => {
      const matchesSearch = !term
        || item.name.toLowerCase().includes(term)
        || item.itemNumber.toLowerCase().includes(term);
      const matchesCategory = !category || item.category === category;
      return matchesSearch && matchesCategory;
    });

    const start = page * pageSize;
    return {
      data: filtered.slice(start, start + pageSize),
      totalCount: filtered.length,
    };
  };

  getItemDetail = async (id: number): Promise<RangePlanningItem> => {
    const item = this.items.find((i) => i.id === id);
    if (!item) throw new Error('Item not found');
    return item;
  };

  createItem = async (data: CreateRangePlanningItemRequest): Promise<RangePlanningItem> => {
    const newItem: RangePlanningItem = {
      id: this.nextId++,
      itemNumber: `ITM-${9000 + this.nextId}`,
      name: data.name,
      category: data.category,
      baseUom: data.baseUom,
      status: ItemLifecycleStatus.PendingReview,
      supplier: {
        supplierName: data.supplierName,
        supplierCode: 'SUP-NEW',
        leadTimeDays: 0,
        minimumOrderQuantity: 0,
        alternateSuppliers: [],
      },
      uomConversions: [],
      pricing: { currency: 'GBP', contractPrice: 0, priceEffectiveDate: new Date().toISOString().slice(0, 10) },
      dietaryAttributes: [],
      carbonRating: CarbonRating.C,
      nutrition: { ...emptyNutritionalInfo, source: NutritionSource.Supplier },
      catalogueAvailability: [],
      recipeUsage: [],
      inventoryMapping: [],
    };
    this.items.push(newItem);
    return newItem;
  };

  setItemStatus = async (id: number, status: RangePlanningItem['status']): Promise<void> => {
    const item = this.items.find((i) => i.id === id);
    if (item) item.status = status;
  };

  flagForSupplierReview = async (_id: number, _reason: string): Promise<void> => {
    // lazy: local-state-only demo action, no persistence/notification backend exists yet.
  };

  toggleCatalogueAvailability = async (id: number, catalogueName: string, available: boolean): Promise<void> => {
    const item = this.items.find((i) => i.id === id);
    if (!item) return;
    const entry = item.catalogueAvailability.find((c) => c.catalogueName === catalogueName);
    if (entry) entry.available = available;
    else item.catalogueAvailability.push({ catalogueName, available });
  };
}
